import React, {useState, useEffect, useRef, useMemo} from "react";
import {observer} from "mobx-react-lite";
import {useStore} from "hooks/useStore";
import {useTranslation} from "react-i18next";
import {useFetch} from "hooks/useFetch";
import {ArrowsAltOutlined, ShrinkOutlined} from "@ant-design/icons";
import {MapContainer, MapTools} from "./CustomerMap.styles";
import useIsFocused from "../../hooks/useIsFocused";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import {CourierIcon} from "components/_defaultComponents/CourierIcon/CourierIcon";
import {courierStatusColors, subcompanyColor} from "../../constants/constants";
import {
    configureLeafletMarkerIcons,
    getTileLayerPreference,
    addTileLayerWithFallback,
    TURKEY_BOUNDS
} from "../../utils/helpers";
import {SubcompanyMarker} from "components/_defaultComponents/SubcompanyMarker/SubcompanyMarker";
import {TileLayerControl} from "components/_defaultComponents/TileLayerControl";
import CourierPopup from "../customer/customerHomePageWithShiftScore/components/CourierPopup";
import {TWENTY_SECONDS_IN_MS} from "../../constants/constants";
import {
    CloseButton,
    ExternalPopup,
    PopupContent
} from "components/customer/customerHomePageWithShiftScore/style/CustomerHomeMapComponent.styles";
import {Content} from "components/dashboard/Marker/PickupMarker/Content";

export const CustomerMap = observer(() => {
    const [center, setCenter] = useState({lat: 41.0082, lng: 28.9784}); // İstanbul default
    const [zoom, setZoom] = useState(11);
    const [mapMode, setMapMode] = useState("relative");
    const stores = useStore();
    const {i18n} = useTranslation();
    const [recall, setRecall] = useState(true);
    const recallRef = useRef(recall);
    recallRef.current = recall;
    const [autoCenter, setAutoCenter] = useState(true);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [selectedSubCompany, setSelectedSubCompany] = useState(null);
    const [popupPosition, setPopupPosition] = useState({x: 0, y: 0});
    const isFocused = useIsFocused();
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const popupRef = useRef(null);
    const tileLayerRef = useRef(null);

    const [isPoolCustomerToShowMapLoading, isPoolCustomerToShowMap] = useFetch({
        fetchFn: stores.poolStore.isPoolCustomerToShowMap.bind(),
        dependencies: [],
        initialCall: true
    });

    const companyOptions = isPoolCustomerToShowMap?.isIntegrationTypeDedicated?.companyIds || [];
    const subCompanyOptions =
        isPoolCustomerToShowMap?.isIntegrationTypeDedicated?.subCompanyIds || [];
    const firstCompanyId = companyOptions[0]?._id;
    const firstSubCompanyId = subCompanyOptions[0]?._id;
    const selectedSubCompanyId = stores.meStore.selectedSubCompanyId;

    const [dedicatedCompanyIds, setDedicatedCompanyIds] = useState([]);
    const [dedicatedSubCompanyIds, setDedicatedSubCompanyIds] = useState([]);

    useEffect(() => {
        setDedicatedCompanyIds([firstCompanyId]);
        setDedicatedSubCompanyIds([firstSubCompanyId]);
    }, [isPoolCustomerToShowMap]);

    const getValidCompanyIds = () => {
        if (dedicatedCompanyIds.length > 0 && dedicatedCompanyIds[0]) {
            return dedicatedCompanyIds;
        }
        return firstCompanyId ? [firstCompanyId] : [];
    };

    const [
        customerMapListLoading,
        customerMapList,
        setCustomerMapList,
        setCustomerMapListLoading
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getCustomerMapList.bind(null, {
            dedicatedCompanyIds: getValidCompanyIds(),
            dedicatedSubCompanyIds: [selectedSubCompanyId]
        }),
        condition:
            !isPoolCustomerToShowMapLoading && isPoolCustomerToShowMap?.isIntegrationTypeDedicated,
        dependencies: [dedicatedCompanyIds, recall, isPoolCustomerToShowMap, selectedSubCompanyId]
    });

    // Close popup when selectedSubCompanyId changes
    useEffect(() => {
        setSelectedCourier(null);
        setSelectedSubCompany(null);
    }, [selectedSubCompanyId]);

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setSelectedCourier(null);
                setSelectedSubCompany(null);
            }
        };

        if (selectedCourier || selectedSubCompany) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [selectedCourier, selectedSubCompany]);

    // Helper function to get status color
    const getStatusColor = status => {
        return courierStatusColors[status] || "#d9d9d9";
    };

    useEffect(() => {
        if (isFocused) {
            let timeout = null;
            timeout = setTimeout(async () => {
                await setRecall(!recallRef.current);
            }, TWENTY_SECONDS_IN_MS);
            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    useEffect(() => {
        setAutoCenter(true);
    }, [dedicatedCompanyIds, dedicatedSubCompanyIds, customerMapList]);

    // Leaflet map initialization
    useEffect(() => {
        if (mapRef.current && !mapInstanceRef.current) {
            // Configure Leaflet marker icons using helper function
            configureLeafletMarkerIcons(L);

            // Türkiye sınırları
            const turkeyBounds = L.latLngBounds(TURKEY_BOUNDS.southWest, TURKEY_BOUNDS.northEast);

            mapInstanceRef.current = L.map(mapRef.current, {
                maxBounds: turkeyBounds,
                maxBoundsViscosity: 1.0,
                minZoom: 7,
                maxZoom: 22
            }).setView([center.lat, center.lng], zoom);

            // Add tile layer with fallback
            tileLayerRef.current = addTileLayerWithFallback(
                L,
                mapInstanceRef.current,
                getTileLayerPreference()
            );

            // Ensure map controls have proper z-index
            const mapContainer = mapInstanceRef.current.getContainer();
            if (mapContainer) {
                const controls = mapContainer.querySelectorAll(".leaflet-control");
                controls.forEach(control => {
                    control.style.zIndex = "1000";
                });
            }

            // Close popups when map is zoomed or panned
            mapInstanceRef.current.on("zoomstart", () => {
                setSelectedCourier(null);
                setSelectedSubCompany(null);
            });

            mapInstanceRef.current.on("movestart", () => {
                setSelectedCourier(null);
                setSelectedSubCompany(null);
            });
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    // Update map center and zoom when they change
    useEffect(() => {
        if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([center.lat, center.lng], zoom);
            // Invalidate size to ensure proper rendering
            setTimeout(() => {
                mapInstanceRef.current.invalidateSize();
            }, 100);
        }
    }, [center, zoom]);

    // Update markers when courier list or subcompany data changes
    useEffect(() => {
        if (mapInstanceRef.current) {
            // Clear existing markers
            markersRef.current.forEach(marker => {
                mapInstanceRef.current.removeLayer(marker);
            });
            markersRef.current = [];

            // Add courier markers
            if (customerMapList && customerMapList.length > 0) {
                customerMapList.forEach((courier, index) => {
                    if (courier?.location?.coordinates) {
                        // Create custom icon with CourierIcon
                        const statusColor = getStatusColor(courier.status);
                        const iconHtml = ReactDOMServer.renderToString(
                            <div
                                style={{
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: statusColor,
                                    borderRadius: "50%",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                    border: "2px solid #fff"
                                }}
                            >
                                <CourierIcon courierType="motorCourier" />
                            </div>
                        );

                        const customIcon = L.divIcon({
                            html: iconHtml,
                            className: "custom-marker-icon courier-marker-icon",
                            iconSize: [32, 32],
                            iconAnchor: [16, 16],
                            popupAnchor: [0, -20]
                        });

                        const marker = L.marker(
                            [courier.location.coordinates[1], courier.location.coordinates[0]],
                            {icon: customIcon}
                        );

                        // Add click event to show popup outside map
                        marker.on("click", e => {
                            const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                e.latlng
                            );
                            setPopupPosition({
                                x: containerPoint.x,
                                y: containerPoint.y
                            });
                            setSelectedCourier(courier);
                        });

                        marker.addTo(mapInstanceRef.current);
                        markersRef.current.push(marker);
                    }
                });
            }

            // Add subcompany markers
            if (isPoolCustomerToShowMap?.isIntegrationTypeDedicated?.subCompanyIds) {
                isPoolCustomerToShowMap.isIntegrationTypeDedicated.subCompanyIds.forEach(
                    subcompany => {
                        if (subcompany?.location?.latitude && subcompany?.location?.longitude) {
                            // Create subcompany marker using SubcompanyMarker component (same as CustomerLeafletMap)
                            const subcompanyIconHtml = SubcompanyMarker({
                                subcompany,
                                isPoolCustomer: true
                            });

                            const customIcon = L.divIcon({
                                html: subcompanyIconHtml,
                                className: "custom-marker-icon",
                                iconSize: [32, 32],
                                iconAnchor: [16, 16],
                                popupAnchor: [0, -16]
                            });

                            const marker = L.marker(
                                [subcompany.location.latitude, subcompany.location.longitude],
                                {icon: customIcon}
                            );

                            // Add click event to show subcompany popup
                            marker.on("click", e => {
                                const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                    e.latlng
                                );
                                setPopupPosition({
                                    x: containerPoint.x,
                                    y: containerPoint.y
                                });
                                setSelectedSubCompany(subcompany);
                                setSelectedCourier(null); // Close courier popup if open
                            });

                            marker.addTo(mapInstanceRef.current);
                            markersRef.current.push(marker);
                        }
                    }
                );
            } else {
                // Fallback: Try to get subcompany data from meStore
                const selectedSubCompany = stores.meStore.subcompanies.find(
                    subCompany => subCompany._id === selectedSubCompanyId
                );

                if (selectedSubCompany && selectedSubCompany.location) {
                    // Create subcompany marker using SubcompanyMarker component (same as CustomerLeafletMap)
                    const subcompanyIconHtml = SubcompanyMarker({
                        subcompany: selectedSubCompany,
                        isPoolCustomer: false
                    });

                    const customIcon = L.divIcon({
                        html: subcompanyIconHtml,
                        className: "custom-marker-icon",
                        iconSize: [32, 32],
                        iconAnchor: [16, 16],
                        popupAnchor: [0, -16]
                    });

                    const marker = L.marker(
                        [
                            selectedSubCompany.location.latitude,
                            selectedSubCompany.location.longitude
                        ],
                        {icon: customIcon}
                    );

                    // Add click event to show subcompany popup
                    marker.on("click", e => {
                        const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                            e.latlng
                        );
                        setPopupPosition({
                            x: containerPoint.x,
                            y: containerPoint.y
                        });
                        setSelectedSubCompany(selectedSubCompany);
                        setSelectedCourier(null); // Close courier popup if open
                    });

                    marker.addTo(mapInstanceRef.current);
                    markersRef.current.push(marker);
                }
            }
        }
    }, [customerMapList, isPoolCustomerToShowMap, selectedSubCompanyId]);

    // Auto-center logic - prioritize courier locations over subcompany locations
    useEffect(() => {
        if (
            (customerMapList && customerMapList.length > 0) ||
            isPoolCustomerToShowMap?.isIntegrationTypeDedicated?.subCompanyIds?.length > 0
        ) {
            if (autoCenter) {
                let allLocations = [];

                // If there are couriers, center ONLY on couriers (ignore subcompanies)
                if (customerMapList && customerMapList.length > 0) {
                    allLocations = customerMapList.map(courier => ({
                        lat: courier.location.coordinates[1],
                        lng: courier.location.coordinates[0]
                    }));
                } else {
                    // No couriers, fall back to subcompany locations
                    if (isPoolCustomerToShowMap?.isIntegrationTypeDedicated?.subCompanyIds) {
                        allLocations = isPoolCustomerToShowMap.isIntegrationTypeDedicated.subCompanyIds.map(
                            subcompany => ({
                                lat: subcompany.location.latitude,
                                lng: subcompany.location.longitude
                            })
                        );
                    }
                }

                if (allLocations.length > 0) {
                    if (
                        allLocations.length === 1 &&
                        customerMapList &&
                        customerMapList.length > 0
                    ) {
                        // Single courier - center directly on it with high zoom
                        setCenter({
                            lat: allLocations[0].lat,
                            lng: allLocations[0].lng
                        });
                        setZoom(15); // High zoom for single courier
                    } else {
                        // Multiple locations - fit bounds
                        const bounds = allLocations.reduce(
                            (acc, location) => {
                                return {
                                    minLat: Math.min(acc.minLat, location.lat),
                                    maxLat: Math.max(acc.maxLat, location.lat),
                                    minLng: Math.min(acc.minLng, location.lng),
                                    maxLng: Math.max(acc.maxLng, location.lng)
                                };
                            },
                            {
                                minLat: 90,
                                maxLat: -90,
                                minLng: 180,
                                maxLng: -180
                            }
                        );
                        const centerLat = (bounds.minLat + bounds.maxLat) / 2;
                        const centerLng = (bounds.minLng + bounds.maxLng) / 2;
                        setCenter({lat: centerLat, lng: centerLng});
                        const latDiff = bounds.maxLat - bounds.minLat;
                        const lngDiff = bounds.maxLng - bounds.minLng;
                        const maxDiff = Math.max(latDiff, lngDiff);
                        let newZoom = 11;
                        if (maxDiff > 0.5) newZoom = 8;
                        else if (maxDiff > 0.2) newZoom = 9;
                        else if (maxDiff > 0.1) newZoom = 10;
                        else if (maxDiff > 0.05) newZoom = 11;
                        else if (maxDiff > 0.02) newZoom = 12;
                        else newZoom = 13;
                        setZoom(newZoom);
                    }
                    setAutoCenter(false);
                } else {
                    setCenter({lat: 41.11, lng: 29.11});
                    setZoom(11);
                    setAutoCenter(false);
                }
            }
        }
    }, [customerMapList, isPoolCustomerToShowMap, autoCenter]);

    useEffect(() => {
        window.addEventListener("keydown", handleEsc);
        return () => {
            window.removeEventListener("keydown", handleEsc);
        };
    }, [mapMode]);

    const handleEsc = event => {
        if (event.keyCode === 27 && mapMode === "fixed") {
            setMapMode("relative");
        }
    };

    const toggleMapMode = () => {
        if (mapMode === "fixed") {
            setMapMode("relative");
        } else {
            setMapMode("fixed");
        }
    };

    // Invalidate map size when map mode changes
    useEffect(() => {
        if (mapInstanceRef.current) {
            setTimeout(() => {
                mapInstanceRef.current.invalidateSize();
            }, 100);
        }
    }, [mapMode]);

    return (
        <div style={{width: "100%"}}>
            <MapContainer mode={mapMode} data-testid="map" style={{width: "100%"}}>
                <MapTools
                    onClick={e => {
                        toggleMapMode();
                        e.currentTarget.blur();
                    }}
                >
                    {mapMode === "relative" ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
                </MapTools>
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <div
                        ref={mapRef}
                        style={{
                            width: "100%",
                            height: "100%",
                            zIndex: 1,
                            minHeight: "100%"
                        }}
                    />
                </div>
                <div style={{position: "absolute", right: "10px", zIndex: 1000}}>
                    <TileLayerControl
                        mapInstance={mapInstanceRef.current}
                        tileLayerRef={tileLayerRef}
                        style={{top: "10px", right: "50px"}}
                    />
                </div>
            </MapContainer>

            {/* External popup */}
            {selectedCourier && (
                <div
                    ref={popupRef}
                    style={{
                        position: "absolute",
                        left: popupPosition.x,
                        top: popupPosition.y,
                        zIndex: 500,
                        backgroundColor: "white",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        padding: "16px",
                        maxWidth: "300px"
                    }}
                >
                    <button
                        onClick={() => setSelectedCourier(null)}
                        style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            background: "none",
                            border: "none",
                            fontSize: "18px",
                            cursor: "pointer"
                        }}
                    >
                        ×
                    </button>
                    <CourierPopup courier={selectedCourier} />
                </div>
            )}

            {/* Subcompany popup */}
            {selectedSubCompany && (
                <ExternalPopup ref={popupRef} x={popupPosition.x} y={popupPosition.y + 40}>
                    <PopupContent>
                        <CloseButton onClick={() => setSelectedSubCompany(null)}>×</CloseButton>
                        <Content pickupInfo={selectedSubCompany} />
                    </PopupContent>
                </ExternalPopup>
            )}
        </div>
    );
});
